import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { Bar, Customized, Scatter, XAxis } from 'recharts'
import useGetVsic from '../../../../../../common/category/useGetVsic'
import { ChartContainer } from '../../../../../../common/chart/chartContainer'
import { Legend } from '../../../../../../common/chart/legend'
import { formatByTheme } from '../../../../../../helpers/theme'
import { CustomXAxisTick } from '../../common/CustomXAxisTick'
import { KEY_DATA, MAP_TYPE_TITLE } from '../../constant'
import { selectData } from '../../store/slice'

const ChartRatioComparisonExport = ({ settingDynamic }) => {
  const {
    DEFAULT_INDUSTRY: vsicDefault,
    COMPARE_INDUSTRY: vsicCompare,
    compareBetweenRatio: compareVal,
  } = settingDynamic

  const data = useSelector(selectData)
  const { vsics } = useGetVsic()

  const defaultData = data[compareVal]?.defaultIndustry?.ratio
  const comparingData = data[compareVal]?.comparingIndustry?.ratio
  const checked = settingDynamic?.ratioChecked || []
  const titleDefaultIndustry = vsics.find(
    (item) => item.id === vsicDefault,
  )?.name
  const titleComparingIndustry =
    vsicDefault === vsicCompare
      ? titleDefaultIndustry + '(1)'
      : vsics.find((item) => item.id === vsicCompare)?.name

  const chartData = useMemo(() => {
    const result = []
    if (checked.includes(KEY_DATA.NET_REVENUE_GROWTH)) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t('companyAnalysis.industryAnalysis.NET_REVENUE_GROWTH'),
        [KEY_DATA.VALUE_1]: defaultData?.[0]?.[KEY_DATA.NET_REVENUE_GROWTH],
        [KEY_DATA.VALUE_2]: defaultData?.[1]?.[KEY_DATA.NET_REVENUE_GROWTH],
        legendKey: KEY_DATA.NET_REVENUE_GROWTH,
      })
    }
    if (checked.includes(KEY_DATA.NET_PROFIT_MARGIN)) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t('companyAnalysis.industryAnalysis.NET_PROFIT_MARGIN'),
        [KEY_DATA.VALUE_1]: defaultData?.[0]?.[KEY_DATA.NET_PROFIT_MARGIN],
        [KEY_DATA.VALUE_2]: defaultData?.[1]?.[KEY_DATA.NET_PROFIT_MARGIN],
        legendKey: KEY_DATA.NET_PROFIT_MARGIN,
      })
    }
    if (checked.includes(KEY_DATA.ROA)) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t('companyAnalysis.industryAnalysis.ROA'),
        [KEY_DATA.VALUE_1]: defaultData?.[0]?.[KEY_DATA.ROA],
        [KEY_DATA.VALUE_2]: defaultData?.[1]?.[KEY_DATA.ROA],
        legendKey: KEY_DATA.ROA,
      })
    }
    if (checked.includes(KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY)) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t(
          'companyAnalysis.industryAnalysis.TOTAL_DEBT_TO_TOTAL_EQUITY',
        ),
        [KEY_DATA.VALUE_1]:
          defaultData?.[0]?.[KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY],
        [KEY_DATA.VALUE_2]:
          defaultData?.[1]?.[KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY],
        legendKey: KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY,
      })
    }
    if (checked.includes(KEY_DATA.CURRENT_RATIO)) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t('companyAnalysis.industryAnalysis.CURRENT_RATIO'),
        [KEY_DATA.VALUE_1]: defaultData?.[0]?.[KEY_DATA.CURRENT_RATIO],
        [KEY_DATA.VALUE_2]: defaultData?.[1]?.[KEY_DATA.CURRENT_RATIO],
        legendKey: KEY_DATA.CURRENT_RATIO,
      })
    }
    if (vsicCompare) {
      if (checked.includes(KEY_DATA.NET_REVENUE_GROWTH)) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t('companyAnalysis.industryAnalysis.NET_REVENUE_GROWTH'),
          [KEY_DATA.VALUE_1]: comparingData?.[0]?.[KEY_DATA.NET_REVENUE_GROWTH],
          [KEY_DATA.VALUE_2]: comparingData?.[1]?.[KEY_DATA.NET_REVENUE_GROWTH],
          legendKey: KEY_DATA.NET_REVENUE_GROWTH,
        })
      }
      if (checked.includes(KEY_DATA.NET_PROFIT_MARGIN)) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t('companyAnalysis.industryAnalysis.NET_PROFIT_MARGIN'),
          [KEY_DATA.VALUE_1]: comparingData?.[0]?.[KEY_DATA.NET_PROFIT_MARGIN],
          [KEY_DATA.VALUE_2]: comparingData?.[1]?.[KEY_DATA.NET_PROFIT_MARGIN],
          legendKey: KEY_DATA.NET_PROFIT_MARGIN,
        })
      }
      if (checked.includes(KEY_DATA.ROA)) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t('companyAnalysis.industryAnalysis.ROA'),
          [KEY_DATA.VALUE_1]: comparingData?.[0]?.[KEY_DATA.ROA],
          [KEY_DATA.VALUE_2]: comparingData?.[1]?.[KEY_DATA.ROA],
          legendKey: KEY_DATA.ROA,
        })
      }
      if (checked.includes(KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY)) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t(
            'companyAnalysis.industryAnalysis.TOTAL_DEBT_TO_TOTAL_EQUITY',
          ),
          [KEY_DATA.VALUE_1]:
            comparingData?.[0]?.[KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY],
          [KEY_DATA.VALUE_2]:
            comparingData?.[1]?.[KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY],
          legendKey: KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY,
        })
      }
      if (checked.includes(KEY_DATA.CURRENT_RATIO)) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t('companyAnalysis.industryAnalysis.CURRENT_RATIO'),
          [KEY_DATA.VALUE_1]: comparingData?.[0]?.[KEY_DATA.CURRENT_RATIO],
          [KEY_DATA.VALUE_2]: comparingData?.[1]?.[KEY_DATA.CURRENT_RATIO],
          legendKey: KEY_DATA.CURRENT_RATIO,
        })
      }
    }

    return result
  }, [defaultData, comparingData, vsicCompare, checked, settingDynamic])

  const groups = [titleDefaultIndustry, titleComparingIndustry]
  const legendSector = [
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_1],
      dataKey: KEY_DATA.VALUE_1,
      color: '#006AA6',
      hide: !checked.includes(KEY_DATA.VALUE_1),
    },
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_2],
      dataKey: KEY_DATA.VALUE_2,
      color: '#FFCD60',
      hide: !checked.includes(KEY_DATA.VALUE_2),
    },
  ]

  return (
    <>
      <ChartContainer
        data={chartData}
        width="100%"
        height={300}
        margin={{
          top: 20,
          right: 10,
          left: 0,
          bottom: 20,
        }}
        yAxis={[
          {
            yAxisId: 'left',
            dataKeys: [KEY_DATA.VALUE_1, KEY_DATA.VALUE_2],
            isLineChart: true,
          },
        ]}
        xAxis={[
          {
            dataKey: 'title',
            interval: 0,
            height: 65,
            padding: { left: 20, right: 20 },
          },
        ]}
      >
        <XAxis
          xAxisId="group"
          dataKey="group"
          axisLine={false}
          tickLine={false}
          height={45}
          interval={0}
          ticks={groups}
          tick={
            <CustomXAxisTick
              titleDefaultIndustry={titleDefaultIndustry}
              isCompare={!!vsicCompare}
              isDynamicReport
            />
          }
        />
        {checked.includes(KEY_DATA.VALUE_1) &&
          checked.includes(KEY_DATA.VALUE_2) && (
            <Customized
              component={(props) => {
                const { formattedGraphicalItems } = props
                const lines = []
                if (
                  !formattedGraphicalItems ||
                  formattedGraphicalItems.length < 2
                )
                  return null
                const value1Points =
                  formattedGraphicalItems[0]?.props?.points || []
                const value2Points =
                  formattedGraphicalItems[1]?.props?.points || []

                for (let i = 0; i < value1Points.length; i++) {
                  const p1 = value1Points[i]
                  const p2 = value2Points[i]

                  if (p1.value1 && p1.value2) {
                    lines.push(
                      <line
                        key={i}
                        x1={p1.cx}
                        y1={p1.cy}
                        x2={p2.cx}
                        y2={p2.cy}
                        stroke={formatByTheme('#9C9C9C')}
                        strokeWidth={2}
                      />,
                    )
                  }
                }
                return <g>{lines}</g>
              }}
            />
          )}
        {legendSector.map((item) => (
          <Scatter
            key={item.dataKey}
            hide={item.hide}
            yAxisId="left"
            dataKey={item.dataKey}
            fill={item.color}
            isAnimationActive={false}
            shape={(props) => {
              if (isNaN(props[item.dataKey])) return null
              return (
                <circle cx={props.cx} cy={props.cy} r={6} fill={props.fill} />
              )
            }}
          />
        ))}
        <Bar
          yAxisId="left"
          dataKey="key1"
          stackId="a"
          fill="#006AA6"
          maxBarSize={35}
          isAnimationActive={false}
        />
        <Bar
          yAxisId="left"
          dataKey="key2"
          stackId="a"
          fill="#FFCD60"
          maxBarSize={35}
          isAnimationActive={false}
        />
      </ChartContainer>
      <Legend
        items={legendSector.map((item) => ({
          ...item,
          isCheckbox: false,
        }))}
        className="flex gap4 justifyContentCenter"
      />
    </>
  )
}

export default ChartRatioComparisonExport
